<template>
  <v-content>
    <v-container my-10>
      <v-layout align-center justify-center>
        <v-progress-circular
          :size="55"
          :width="4"
          indeterminate
          color="primary"
          class="mt-12"
          v-if="loading"
        ></v-progress-circular>
        <v-card v-if="!loading" max-width="650px">
          <v-card-title>
            <span class="headline">
              {{ $t("builder.parts.details.preview") }}</span
            >
            <v-spacer></v-spacer>
            <v-card-actions>
              <v-btn
                text
                color="primary"
                v-if="editedItem.is_editable"
                @click="edit()"
                ><v-icon small data-cy="itemDetailsEdit" class="mx-2"
                  >mdi-pencil</v-icon
                >{{ $t("builder.parts.details.edit") }}</v-btn
              >
              <v-btn
                text
                color="red"
                @click="deleteFn()"
                v-if="editedItem.is_removable"
              >
                <v-icon small class="mx-2">mdi-delete</v-icon
                >{{ $t("builder.parts.details.delete") }}</v-btn
              >
            </v-card-actions>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" sm="6" md="8" lg="12">
                  <v-card elevation="0" pa-1 ma-1>
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.productName")
                    }}</v-card-title>
                    <v-card-subtitle> {{ editedItem.name }}</v-card-subtitle>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="8" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.manufacturerName")
                    }}</v-card-title>
                    <v-card-subtitle v-if="editedItem.vendor">
                      {{ editedItem.vendor.name }}</v-card-subtitle
                    >
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="8" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.documents")
                    }}</v-card-title>
                    <DocumentPicker
                      :documents="editedItem.documents"
                      :preview="true"
                      :unavailable-documents="[]"
                      :readonly="true"
                      feature="template"
                    />
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="8" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.itemCategories")
                    }}</v-card-title>
                    <v-card-subtitle>
                      <v-chip
                        v-bind:key="index"
                        v-for="(
                          category, index
                        ) in editedItem.prettified_categories"
                        class="mx-1"
                      >
                        <v-icon class="mr-2" v-if="category.icon">{{
                          category.icon
                        }}</v-icon>
                        <span :class="{ 'text-capitalize': !!category.icon }">{{
                          category.category
                        }}</span>
                      </v-chip>
                    </v-card-subtitle>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="8" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.warrantyPeriod")
                    }}</v-card-title>
                    <v-card-subtitle v-if="editedItem.warranty_months">
                      {{ editedItem.warranty_months }}
                      {{ $t("builder.parts.details.months") }}</v-card-subtitle
                    >
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="8" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.extendedWarrantyPeriod")
                    }}</v-card-title>
                    <v-card-subtitle v-if="editedItem.extended_warranty_months">
                      {{ editedItem.extended_warranty_months }}
                      {{ $t("builder.parts.details.months") }}</v-card-subtitle
                    >
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="8" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.sku")
                    }}</v-card-title>
                    <v-card-subtitle> {{ editedItem.sku }}</v-card-subtitle>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="8" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.productCode")
                    }}</v-card-title>
                    <v-card-subtitle>
                      {{
                        editedItem.manufacturer_product_code
                      }}</v-card-subtitle
                    >
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="8" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.description")
                    }}</v-card-title>
                    <v-card-subtitle>
                      {{ editedItem.description }}
                    </v-card-subtitle>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="8" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.visibility")
                    }}</v-card-title>
                    <v-card-subtitle>
                      {{ editedItem.crm_group.name }}
                    </v-card-subtitle>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("builder.parts.details.serviceProtocol")
                    }}</v-card-title>
                    <v-row
                      v-for="service in editedItem.crm_services"
                      :key="service.uuid"
                    >
                      <v-col cols="1" sm="1" md="1" lg="1">
                        <v-icon class="ml-5 mt-4 align-center"
                          >mdi-wrench-outline</v-icon
                        >
                      </v-col>
                      <v-col cols="11" sm="11" md="11" lg="11">
                        <v-card-title
                          class="body-2 text-wrap"
                          style="word-break: break-word"
                          >{{ service.short_task }}</v-card-title
                        >
                        <v-card-title class="body-2 mb-0 pb-0">
                          {{
                            $t("builder.parts.details.description")
                          }}</v-card-title
                        >
                        <div
                          v-if="
                            service.long_task && service.long_task.length !== 0
                          "
                        >
                          <v-card-subtitle
                            class="pa-0 ma-0 ml-4"
                            v-for="(desc, index) in getDescriptionArray(
                              service.long_task
                            )"
                            :key="index"
                          >
                            {{ desc }}</v-card-subtitle
                          >
                        </div>
                        <div v-else>
                          <v-card-subtitle
                            class="pa-0 ma-0 ml-4"
                            v-for="(subtask, index) in service.subtasks"
                            :key="index"
                          >
                            {{ subtask.description }}</v-card-subtitle
                          >
                        </div>
                        <div
                          v-for="interval in service.intervals"
                          :key="interval.uuid"
                        >
                          <v-card-title class="body-2">
                            {{
                              $t("builder.parts.details.interval")
                            }}</v-card-title
                          >
                          <v-card-subtitle>{{
                            intervalString(interval)
                          }}</v-card-subtitle>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              data-cy="itemDetailsCancel"
              @click="close"
              text
              >{{ $t("builder.parts.details.cancel") }}</v-btn
            >
          </v-card-actions>
        </v-card>
        <delete-dialog
          :dialog="deleteDialog"
          :item="itemName"
          @agree="remove"
          v-show="false"
        />
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
import api from "@/utils/api.js";
import DocumentPicker from "@/components/pickers/DocumentPicker";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import categories from "@/utils/categories.js";
export default {
  components: { DocumentPicker, DeleteDialog },
  data: () => ({
    loading: true,
    deleteDialog: false,
    itemName: null,
    categoryOptions: [],
    editedItem: {
      uuid: null,
      name: "",
      categories: [],
      crm_group_uuid: null,
      crm_services: [],
      is_boat_specific: false,
      start_date: new Date(),
      vendor: null,
      vendor_uuid: null,
      warranty_months: null,
      extended_warranty_months: null,
    },
  }),

  computed: {},

  created() {
    this.categoryOptions = categories.categoryOptions();
    api.getItem(this.$route.params.id).then((resp) => {
      resp.data.prettified_categories = [];
      resp.data.categories.forEach((category) => {
        var prettified_category = this.categoryOptions.find(
          (categoryOption) => category === categoryOption.category
        );
        if (typeof prettified_category !== "undefined") {
          resp.data.prettified_categories.push(prettified_category);
        } else {
          resp.data.prettified_categories.push({
            category: category,
            // icon: "mdi-cancel",
          });
        }
      });
      this.editedItem = resp.data;
      this.loading = false;
    });
  },
  methods: {
    getDescriptionArray(val) {
      return val.split(",");
    },
    intervalString(interval) {
      let source, periodic, postfix;
      if (interval.source === "MANUAL")
        return this.$t("builder.parts.details.custom");

      if (interval.source === "ITEM_AGE_MONTHS")
        source = this.$t("builder.parts.details.itemAge");
      else if (interval.source === "ENGINE_HOURS")
        source = this.$t("builder.parts.details.engineHours");
      else if (interval.source === "MILEAGE")
        source = this.$t("builder.parts.details.boatMileage");
      else if (interval.source === "SAILING_HOURS")
        source = this.$t("builder.parts.details.sailingHours");
      else if (interval.source === "RUNNING_HOURS")
        source = this.$t("builder.parts.details.runningHours");

      if (interval.periodic) periodic = this.$t("builder.parts.details.every");
      else periodic = this.$t("builder.parts.details.at");

      if (interval.source === "ITEM_AGE_MONTHS")
        postfix = this.$t("builder.parts.details.months");
      else if (interval.source === "ENGINE_HOURS")
        postfix = this.$t("builder.parts.details.hours");
      else if (interval.source === "SAILING_HOURS")
        postfix = this.$t("builder.parts.details.hours");
      else if (interval.source === "RUNNING_HOURS")
        postfix = this.$t("builder.parts.details.hours");
      else if (interval.source === "MILEAGE")
        postfix = this.$t("builder.parts.details.nm");
      // let postfix = interval.source === "ITEM_AGE_MONTHS" ? "months" : "hours";
      return `${source} ${periodic} ${interval.value} ` + postfix;
    },
    close() {
      this.$router.push({
        name: "items",
      });
    },
    edit() {
      this.$router.push({
        name: "itemsEdit",
        params: { id: this.$route.params.id },
      });
    },
    deleteFn() {
      this.deleteDialog = true;
      this.itemName = this.editedItem.name;
    },

    remove(agreed) {
      if (agreed) {
        api.deleteItem(this.editedItem.uuid).then((resp) => {
          this.$router.push({
            name: "items",
          });
        });
      }
      this.deleteDialog = false;
    },
  },
};
</script>
<style lang="scss" scoped></style>
